import { createContext, useContext } from 'react';

import type { FontId } from './text';
import type {
  ArrowColor,
  BoardSpec,
  CharacterColor,
  HighlightColor,
  ThemeId,
} from './types';

// A board's look: paper, ink, accent colours, fonts, character fills and how
// elements enter. Layouts and primitives read it instead of fixed constants,
// so a look is an entry here, not new templates.

/** The writing systems boards draw (EXPLAINER_LANGUAGE_CODES). */
export type Script =
  | 'latin'
  | 'cyrillic'
  | 'greek'
  | 'vietnamese'
  | 'ja'
  | 'zh-hans'
  | 'zh-hant'
  | 'ko'
  | 'devanagari'
  | 'arabic';

/** Alphabets the looks' own fonts cover. */
export type LatinScript = 'latin' | 'cyrillic' | 'greek' | 'vietnamese';
/** Writing systems drawn by script fonts after the look's Latin font. */
export type OtherScript = Exclude<Script, LatinScript>;

export const isLatinScript = (script: Script): script is LatinScript =>
  script === 'latin' ||
  script === 'cyrillic' ||
  script === 'greek' ||
  script === 'vietnamese';

/** Right-to-left writing systems: their boards are laid out mirrored. */
export const isRtlScript = (script: Script) => script === 'arabic';

/** The premium looks, each drawn by its own renderer in looks/. */
export type PremiumLook =
  | 'glass'
  | 'kinetic'
  | 'paper'
  | 'neon'
  | 'evidence'
  | 'newsprint';

export interface BoardTheme {
  id: ThemeId;
  /** The font for each alphabet (handwriting fonts cover different ones). */
  fonts: Record<LatinScript, FontId>;
  /**
   * Other writing systems: fonts drawn after the Latin font (CSS fallback
   * order), the look's style first, a full-coverage font last.
   */
  scriptFonts: Record<OtherScript, FontId[]>;
  /** Board background. */
  paper: string;
  /** Background image over the paper colour, a public/ path per aspect. */
  texture?: { landscape: string; portrait: string };
  /** Text, outlines, black arrows. */
  ink: string;
  /** Highlighted words, red and green arrows. */
  accents: Record<HighlightColor, string>;
  /** Chapter cards. */
  chapterPaper: string;
  chapterInk: string;
  /** Chapter cards' background image (default: none). */
  chapterTexture?: { landscape: string; portrait: string };
  characterFills: Record<CharacterColor, string>;
  /**
   * Cards behind text (quiz options, ranking slots, versus sides): a fill
   * the ink reads on. Neon draws cards as tubes in this colour.
   */
  panel: string;
  /** Text on a card filled with an accent (a quiz's right answer). */
  onAccent: string;
  /** Character eyes. */
  eyes: string;
  /**
   * `pop`: elements spring in. `draw`: a pen draws them stroke by stroke,
   * timed so each finishes before the next narrated element is due.
   */
  entrance: 'pop' | 'draw';
  /** What draws (draw looks); none: lines appear without a hand. */
  pen?: 'marker' | 'chalk' | 'ballpoint';
  /**
   * Highlighted words: in an accent colour (default), or the ink with a
   * highlighter swipe of the accent colour behind them.
   */
  highlighter?: Record<HighlightColor, string>;
  /** Drawn looks: what wipes the board at its end (default: an eraser). */
  wipe?: 'eraser' | 'sweep';
  /**
   * Drawn looks: an outline on text, as a share of its size, for thin
   * lettering (it doesn't change the text's width).
   */
  textStroke?: number;
  /**
   * Drawn looks: drawn under the header as it's written: a draftsman's
   * dimension line, or a pen underline in the red accent.
   */
  headerRule?: 'dimension' | 'underline';
  /**
   * Icons: `flat` colour (Fluent Emoji Flat), `line` art in ink (Fluent
   * Emoji High Contrast), or `wash`: line art over a pale flat icon.
   */
  icons: 'flat' | 'line' | 'wash';
  /**
   * Characters: coloured `bean`s (the Explains 101 style), or `figure`s,
   * Fluent Emoji people and animals (figures.ts), laid out in a square box.
   */
  characters?: 'bean' | 'figure';
  /**
   * Premium looks render through their own layer (looks/), with a camera,
   * transitions and idle motion; the fields above still drive the layout.
   */
  look?: PremiumLook;
}

const CLASSIC_FILLS: Record<CharacterColor, string> = {
  green: '#22B14C',
  gray: '#C9C9C9',
  blue: '#94D6F2',
  purple: '#C65BD1',
  red: '#FF2B36',
  orange: '#FF8A1F',
  brown: '#B97550',
  beige: '#F1E4AE',
};

/** Plain sans fonts (classic, glass, kinetic). */
const SANS_SCRIPT_FONTS: Record<OtherScript, FontId[]> = {
  ja: ['notosansjp'],
  'zh-hans': ['notosanssc'],
  'zh-hant': ['notosanstc'],
  ko: ['notosanskr'],
  devanagari: ['notosansdevanagari'],
  arabic: ['notosansarabic'],
};

/** Handwriting for other writing systems (whiteboard, blueprint, notebook). */
const HANDWRITING_SCRIPT_FONTS: Record<OtherScript, FontId[]> = {
  ja: ['yomogi', 'notosansjp'],
  'zh-hans': ['lxgwmarkergothic', 'notosanssc'],
  'zh-hant': ['lxgwmarkergothic', 'notosanstc'],
  ko: ['gaegu', 'notosanskr'],
  devanagari: ['kalam'],
  arabic: ['marhey'],
};

export const THEMES: Record<ThemeId, BoardTheme> = {
  classic: {
    id: 'classic',
    fonts: {
      latin: 'inter',
      cyrillic: 'inter',
      greek: 'inter',
      vietnamese: 'inter',
    },
    scriptFonts: SANS_SCRIPT_FONTS,
    paper: '#fff',
    ink: '#151515',
    accents: { red: '#D62828', green: '#4E8A2E' },
    chapterPaper: '#000',
    chapterInk: '#fff',
    characterFills: CLASSIC_FILLS,
    panel: '#F1F1F1',
    onAccent: '#FFFFFF',
    eyes: '#151515',
    entrance: 'pop',
    icons: 'flat',
  },
  whiteboard: {
    id: 'whiteboard',
    fonts: {
      latin: 'caveat',
      cyrillic: 'caveat',
      greek: 'mynerve',
      vietnamese: 'pangolin',
    },
    scriptFonts: HANDWRITING_SCRIPT_FONTS,
    paper: '#FBFBF8',
    texture: {
      landscape: 'explainer-textures/whiteboard-16x9.jpg',
      portrait: 'explainer-textures/whiteboard-9x16.jpg',
    },
    ink: '#1E2227',
    accents: { red: '#E03131', green: '#2B8A3E' },
    chapterPaper: '#FBFBF8',
    chapterInk: '#1D4ED8',
    chapterTexture: {
      landscape: 'explainer-textures/whiteboard-16x9.jpg',
      portrait: 'explainer-textures/whiteboard-9x16.jpg',
    },
    characterFills: CLASSIC_FILLS,
    panel: '#E6E9EC',
    onAccent: '#1E2227',
    eyes: '#1E2227',
    entrance: 'draw',
    pen: 'marker',
    icons: 'wash',
  },
  chalkboard: {
    id: 'chalkboard',
    fonts: {
      latin: 'pangolin',
      cyrillic: 'pangolin',
      greek: 'mynerve',
      vietnamese: 'pangolin',
    },
    scriptFonts: {
      ja: ['kleeone', 'notosansjp'],
      'zh-hans': ['lxgwwenkaitc', 'notosanssc'],
      'zh-hant': ['lxgwwenkaitc', 'notosanstc'],
      ko: ['gamjaflower', 'notosanskr'],
      devanagari: ['kalam'],
      arabic: ['marhey'],
    },
    paper: '#25382E',
    texture: {
      landscape: 'explainer-textures/chalkboard-16x9.jpg',
      portrait: 'explainer-textures/chalkboard-9x16.jpg',
    },
    ink: '#F2F0E6',
    accents: { red: '#FF9B94', green: '#A9E5A0' },
    chapterPaper: '#25382E',
    chapterInk: '#F6DC72',
    chapterTexture: {
      landscape: 'explainer-textures/chalkboard-16x9.jpg',
      portrait: 'explainer-textures/chalkboard-9x16.jpg',
    },
    characterFills: {
      green: '#8FD694',
      gray: '#C9C9C2',
      blue: '#8CCBF0',
      purple: '#CDA6EC',
      red: '#F59A9A',
      orange: '#F7B977',
      brown: '#CBA27F',
      beige: '#EFE3B5',
    },
    panel: '#33493D',
    onAccent: '#F2F0E6',
    eyes: '#25382E',
    entrance: 'draw',
    pen: 'chalk',
    icons: 'line',
  },
  glass: {
    id: 'glass',
    fonts: {
      latin: 'manrope',
      cyrillic: 'manrope',
      greek: 'manrope',
      vietnamese: 'manrope',
    },
    scriptFonts: SANS_SCRIPT_FONTS,
    paper: '#0A0F2C',
    ink: '#F5F7FF',
    accents: { red: '#FF8FB1', green: '#6FF2D2' },
    chapterPaper: '#0A0F2C',
    chapterInk: '#FFFFFF',
    characterFills: {
      green: '#3DDC97',
      gray: '#C7D0E0',
      blue: '#5AA9FF',
      purple: '#A98BFF',
      red: '#FF6F8E',
      orange: '#FF9F45',
      brown: '#C98B5B',
      beige: '#FFE08A',
    },
    panel: '#1C2452',
    onAccent: '#0A0F2C',
    eyes: '#1B1F3B',
    entrance: 'pop',
    icons: 'flat',
    characters: 'figure',
    look: 'glass',
  },
  kinetic: {
    id: 'kinetic',
    fonts: {
      latin: 'intertight',
      cyrillic: 'intertight',
      greek: 'intertight',
      vietnamese: 'intertight',
    },
    scriptFonts: SANS_SCRIPT_FONTS,
    paper: '#111111',
    ink: '#FFFFFF',
    accents: { red: '#FF4D3D', green: '#C8FF3D' },
    chapterPaper: '#FF4D3D',
    chapterInk: '#111111',
    characterFills: CLASSIC_FILLS,
    panel: '#242424',
    onAccent: '#111111',
    eyes: '#111111',
    entrance: 'pop',
    icons: 'flat',
    look: 'kinetic',
  },
  paper: {
    id: 'paper',
    fonts: {
      latin: 'nunito',
      cyrillic: 'nunito',
      greek: 'manrope',
      vietnamese: 'nunito',
    },
    scriptFonts: {
      ja: ['zenmarugothic', 'notosansjp'],
      'zh-hans': ['huninn', 'notosanssc'],
      'zh-hant': ['huninn', 'notosanstc'],
      ko: ['dohyeon', 'notosanskr'],
      devanagari: ['baloo2'],
      arabic: ['baloobhaijaan2'],
    },
    paper: '#2F6E72',
    ink: '#2A2421',
    accents: { red: '#D9472B', green: '#2F8F4E' },
    chapterPaper: '#F2B53A',
    chapterInk: '#2A2421',
    characterFills: {
      green: '#77C25C',
      gray: '#BDBAB2',
      blue: '#5FA8E8',
      purple: '#9D78D2',
      red: '#E5573A',
      orange: '#F39A45',
      brown: '#A97852',
      beige: '#F3DDA4',
    },
    panel: '#FFF4DC',
    onAccent: '#FFFDF7',
    eyes: '#2A2421',
    entrance: 'pop',
    icons: 'flat',
    characters: 'figure',
    look: 'paper',
  },
  neon: {
    id: 'neon',
    fonts: {
      latin: 'tiltneon',
      cyrillic: 'manrope',
      greek: 'manrope',
      vietnamese: 'tiltneon',
    },
    scriptFonts: {
      ja: ['zenmarugothic', 'notosansjp'],
      'zh-hans': ['notosanssc'],
      'zh-hant': ['notosanstc'],
      ko: ['notosanskr'],
      devanagari: ['baloo2'],
      arabic: ['baloobhaijaan2'],
    },
    paper: '#08070F',
    ink: '#F6F2FF',
    accents: { red: '#FF4FA7', green: '#52FF9A' },
    chapterPaper: '#08070F',
    chapterInk: '#FFE066',
    characterFills: {
      green: '#52FF9A',
      gray: '#D8D4FF',
      blue: '#45E3FF',
      purple: '#B98CFF',
      red: '#FF4FA7',
      orange: '#FFA94D',
      brown: '#FFB98A',
      beige: '#FFE066',
    },
    panel: '#9B93E0',
    onAccent: '#F6F2FF',
    eyes: '#F6F2FF',
    entrance: 'pop',
    icons: 'line',
    characters: 'figure',
    look: 'neon',
  },
  evidence: {
    id: 'evidence',
    // A typewriter face; Noto Sans Mono for the alphabets it lacks.
    fonts: {
      latin: 'courierprime',
      cyrillic: 'notosansmono',
      greek: 'notosansmono',
      vietnamese: 'notosansmono',
    },
    scriptFonts: SANS_SCRIPT_FONTS,
    // The cork board (the look draws its texture over this).
    paper: '#A87A4A',
    ink: '#25221E',
    accents: { red: '#C4161C', green: '#2E7D46' },
    chapterPaper: '#A87A4A',
    chapterInk: '#25221E',
    // Inks for charts on graph paper; fills for beans when art is missing.
    characterFills: {
      green: '#4E9A5A',
      gray: '#B9B4AA',
      blue: '#3B6EA8',
      purple: '#7A4E9C',
      red: '#C4161C',
      orange: '#D9822B',
      brown: '#9A6B45',
      beige: '#E8D9B0',
    },
    // Cards are index cards.
    panel: '#FBF8F0',
    onAccent: '#FFFFFF',
    eyes: '#25221E',
    entrance: 'pop',
    icons: 'flat',
    characters: 'figure',
    look: 'evidence',
  },
  newsprint: {
    id: 'newsprint',
    // A condensed headline face; Manrope for Greek (Oswald has none).
    fonts: {
      latin: 'oswald',
      cyrillic: 'oswald',
      greek: 'manrope',
      vietnamese: 'oswald',
    },
    scriptFonts: SANS_SCRIPT_FONTS,
    // The newspaper page (the look draws its texture over this).
    paper: '#EAE5D7',
    ink: '#151515',
    accents: { red: '#D62F2F', green: '#178A55' },
    chapterPaper: '#EAE5D7',
    chapterInk: '#151515',
    characterFills: {
      green: '#3E9B5A',
      gray: '#B8B4AC',
      blue: '#2F66D0',
      purple: '#7B4FA8',
      red: '#E8483A',
      orange: '#F08A2B',
      brown: '#9A6B45',
      beige: '#EED9A8',
    },
    panel: '#FFFDF8',
    onAccent: '#FFFFFF',
    eyes: '#151515',
    entrance: 'pop',
    icons: 'flat',
    characters: 'figure',
    look: 'newsprint',
  },
  blueprint: {
    id: 'blueprint',
    // A draftsman's hand; Noto Sans Mono for the alphabets it lacks.
    fonts: {
      latin: 'architectsdaughter',
      cyrillic: 'notosansmono',
      greek: 'notosansmono',
      vietnamese: 'notosansmono',
    },
    scriptFonts: HANDWRITING_SCRIPT_FONTS,
    paper: '#1A4880',
    texture: {
      landscape: 'explainer-textures/blueprint-16x9.jpg',
      portrait: 'explainer-textures/blueprint-9x16.jpg',
    },
    ink: '#F2F7FF',
    // Correction pencil and approval green, light enough for the blue.
    accents: { red: '#FFB199', green: '#A8F0C0' },
    chapterPaper: '#1A4880',
    chapterInk: '#FFE58A',
    chapterTexture: {
      landscape: 'explainer-textures/blueprint-16x9.jpg',
      portrait: 'explainer-textures/blueprint-9x16.jpg',
    },
    characterFills: {
      green: '#4E8EC9',
      gray: '#3F6EA6',
      blue: '#5B9BD8',
      purple: '#6C84C9',
      red: '#C77A86',
      orange: '#C99A5A',
      brown: '#8C7A6A',
      beige: '#8FB3D9',
    },
    panel: '#24579A',
    onAccent: '#FFFFFF',
    eyes: '#F2F7FF',
    entrance: 'draw',
    icons: 'line',
    wipe: 'sweep',
    textStroke: 0.022,
    headerRule: 'dimension',
  },
  notebook: {
    id: 'notebook',
    fonts: {
      latin: 'patrickhand',
      cyrillic: 'caveat',
      greek: 'mynerve',
      vietnamese: 'patrickhand',
    },
    scriptFonts: HANDWRITING_SCRIPT_FONTS,
    paper: '#FBF9F2',
    texture: {
      landscape: 'explainer-textures/notebook-16x9.jpg',
      portrait: 'explainer-textures/notebook-9x16.jpg',
    },
    // Blue ballpoint.
    ink: '#1F3A8A',
    accents: { red: '#D7263D', green: '#1E8A4C' },
    highlighter: {
      red: 'rgba(255,120,170,0.5)',
      green: 'rgba(150,235,110,0.55)',
    },
    chapterPaper: '#FBF9F2',
    chapterInk: '#1F3A8A',
    chapterTexture: {
      landscape: 'explainer-textures/notebook-16x9.jpg',
      portrait: 'explainer-textures/notebook-9x16.jpg',
    },
    characterFills: CLASSIC_FILLS,
    panel: '#EEF1F8',
    onAccent: '#1F3A8A',
    eyes: '#1F3A8A',
    entrance: 'draw',
    pen: 'ballpoint',
    icons: 'wash',
    headerRule: 'underline',
    // Ink can't be erased: the board is swept away instead.
    wipe: 'sweep',
  },
};

export const DEFAULT_THEME = THEMES.classic;

export const boardTheme = (board: BoardSpec): BoardTheme =>
  THEMES[board.theme ?? 'classic'] ?? DEFAULT_THEME;

const CYRILLIC_LANGS = new Set(['ru', 'uk', 'bg', 'sr', 'mk', 'be', 'kk']);

/** Every string in a spec (texts, labels, names). */
export function specText(value: unknown, out: string[] = []): string[] {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) value.forEach((v) => specText(v, out));
  else if (value && typeof value === 'object') {
    Object.values(value).forEach((v) => specText(v, out));
  }
  return out;
}

// Frequent characters that exist in one of the two Chinese scripts only.
const SIMPLIFIED = new Set(
  '们这说个来会时国学长开东车书见门问间电话语经业现发点为与对后过还进动种实当体关机应报头别马产样万亿钱银货资价买卖帮让认识区读写听亲爱农带热风飞鸟鱼龙灯网络线级设计划华历变难将么无气并于从众传杂场环观题页处两条务类总统际运达选边远连',
);
const TRADITIONAL = new Set(
  '們這說個來會時國學長開東車書見門問間電話語經業現發點為與對後過還進動種實當體關機應報頭別馬產樣萬億錢銀貨資價買賣幫讓認識區讀寫聽親愛農帶熱風飛鳥魚龍燈網絡線級設計劃華歷變難將麼無氣並於從眾傳雜場環觀題頁處兩條務類總統際運達選邊遠連',
);

/** Simplified or Traditional Chinese, from the characters used. */
export function chineseScript(text: string): 'zh-hans' | 'zh-hant' {
  let simplified = 0;
  let traditional = 0;
  for (const c of text) {
    if (SIMPLIFIED.has(c)) simplified++;
    else if (TRADITIONAL.has(c)) traditional++;
  }
  return traditional > simplified ? 'zh-hant' : 'zh-hans';
}

/** The writing system of a text (no language known). */
export function textScript(text: string): Script {
  if (/[\u3040-\u30ff\u31f0-\u31ff]/.test(text)) return 'ja';
  if (/[\uac00-\ud7a3\u1100-\u11ff\u3130-\u318f]/.test(text)) return 'ko';
  if (/[\u3400-\u4dbf\u4e00-\u9fff]/.test(text)) return chineseScript(text);
  if (/[\u0900-\u097f]/.test(text)) return 'devanagari';
  if (/[\u0600-\u06ff\u0750-\u077f]/.test(text)) return 'arabic';
  if (/[Ͱ-Ͽἀ-῿]/.test(text)) return 'greek';
  // Letters only Vietnamese uses (tone-marked vowels, ơ, ư).
  if (/[Ạ-ỹƠơƯư]/.test(text)) {
    return 'vietnamese';
  }
  if (/[Ѐ-ӿ]/.test(text)) return 'cyrillic';
  return 'latin';
}

/** The writing system of a language (ISO 639-1), or undefined if Latin-like. */
export function languageScript(lang: string, text = ''): Script {
  const base = lang.toLowerCase().split(/[-_]/)[0];
  if (base === 'ja') return 'ja';
  if (base === 'ko') return 'ko';
  if (base === 'zh') {
    if (/hant|tw|hk|mo/i.test(lang)) return 'zh-hant';
    if (/hans|cn|sg/i.test(lang)) return 'zh-hans';
    return chineseScript(text);
  }
  if (base === 'hi' || base === 'mr' || base === 'ne') return 'devanagari';
  if (base === 'ar' || base === 'fa' || base === 'ur') return 'arabic';
  if (base === 'el') return 'greek';
  if (base === 'vi') return 'vietnamese';
  if (CYRILLIC_LANGS.has(base)) return 'cyrillic';
  return 'latin';
}

/**
 * The board's writing system: from its narration language when stamped,
 * else read from its text (boards made without `lang`).
 */
export function boardScript(board: BoardSpec): Script {
  const text = specText(board).join(' ');
  return board.lang ? languageScript(board.lang, text) : textScript(text);
}

/** The board's fonts: the look's font, then the writing system's. */
export function boardFonts(
  board: BoardSpec,
  theme = boardTheme(board),
): { font: FontId; fallbacks: FontId[]; script: Script } {
  const script = boardScript(board);
  if (isLatinScript(script)) {
    return { font: theme.fonts[script], fallbacks: [], script };
  }
  return {
    font: theme.fonts.latin,
    fallbacks: theme.scriptFonts[script],
    script,
  };
}

export const boardFont = (board: BoardSpec, theme = boardTheme(board)) =>
  boardFonts(board, theme).font;

/** A theme with the board's fonts chosen and loaded. */
export interface ResolvedTheme extends BoardTheme {
  font: FontId;
  /** Fonts for the board's writing system, after `font`. */
  fallbacks: FontId[];
  script: Script;
  /** Laid out right to left (mirrored). */
  rtl: boolean;
  /** The CSS font-family list of `font` and `fallbacks`. */
  fontFamily: string;
}

export const ThemeContext = createContext<ResolvedTheme>({
  ...DEFAULT_THEME,
  font: 'inter',
  fallbacks: [],
  script: 'latin',
  rtl: false,
  fontFamily: 'Explainer Inter',
});

export const useTheme = () => useContext(ThemeContext);

export const arrowColor = (theme: BoardTheme, color?: ArrowColor) =>
  color === 'red' || color === 'green' ? theme.accents[color] : theme.ink;

/** Background colour behind a board (chapter cards have their own). */
export const boardBackground = (
  board: BoardSpec,
  theme: BoardTheme = boardTheme(board),
) => (board.template === 'chapter' ? theme.chapterPaper : theme.paper);

/** Background image of a board, a public/ path, if its look has one. */
export const boardTexture = (
  board: BoardSpec,
  theme: BoardTheme = boardTheme(board),
) => {
  const texture =
    board.template === 'chapter' ? theme.chapterTexture : theme.texture;
  if (!texture) return undefined;
  return board.aspect === '9:16' ? texture.portrait : texture.landscape;
};
